import React, { useEffect, useRef, useState } from 'react';
import { CrudList, CrudStyles } from '../shared/Crud';
import costCenterService from '../../services/costCenterService';
import { COST_CENTER_LIST_COLUMNS } from './costCenterListColumns';
import CostCenterSearchFilters from './CostCenterSearchFilters';
import CostCenterCreateForm from './CostCenterCreateForm';
import CostCenterDetailForm from './CostCenterDetailForm';

const { Button: CrudOutlineButton } = CrudStyles;

const FILTER_DEBOUNCE_MS = 500;

/**
 * Listagem paginada de centros de custo por cliente (reutilizável em qualquer tela).
 * A empresa de transporte deve passar o `customerId` da pessoa jurídica.
 */
export default function CostCenterListPanel({
    customerId,
    title = 'Centros de custo',
    idPrefix = 'cost-center',
    pageSize = 10,
    sort = 'name,asc',
}) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);

    const [localName, setLocalName] = useState('');
    const [localCode, setLocalCode] = useState('');
    const [localActive, setLocalActive] = useState('true');

    const [searchName, setSearchName] = useState('');
    const [searchCode, setSearchCode] = useState('');
    const [searchActive, setSearchActive] = useState('true');

    const [pagination, setPagination] = useState({
        page: 0,
        totalPages: 0,
        first: true,
        last: true,
    });
    const [reloadTick, setReloadTick] = useState(0);
    const [creating, setCreating] = useState(false);
    const [detailItem, setDetailItem] = useState(null);

    const isInitialMount = useRef(true);

    useEffect(() => {
        setLocalName('');
        setLocalCode('');
        setLocalActive('true');
        setSearchName('');
        setSearchCode('');
        setSearchActive('true');
        setPagination({ page: 0, totalPages: 0, first: true, last: true });
        setLoadError(null);
        setCreating(false);
        setDetailItem(null);
    }, [customerId]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        const t = setTimeout(() => {
            setSearchName(localName);
            setSearchCode(localCode);
            setSearchActive(localActive);
            setPagination((p) => ({ ...p, page: 0 }));
        }, FILTER_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [localName, localCode, localActive]);

    useEffect(() => {
        if (customerId == null || customerId === '') {
            setData([]);
            setLoading(false);
            return;
        }

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const activeParam = searchActive === 'true';
                const result = await costCenterService.list(customerId, {
                    page: pagination.page,
                    size: pageSize,
                    sort,
                    ...(searchName.trim() ? { name: searchName.trim() } : {}),
                    ...(searchCode.trim() ? { code: searchCode.trim() } : {}),
                    active: activeParam,
                });
                if (cancelled) return;
                const content = result.content || [];
                const apiTotal =
                    result.page?.totalPages != null && result.page.totalPages !== ''
                        ? Number(result.page.totalPages)
                        : null;
                const totalPages =
                    apiTotal != null && Number.isFinite(apiTotal)
                        ? Math.max(0, apiTotal)
                        : content.length === 0
                          ? 0
                          : 1;
                const rawNumber = result.page?.number ?? 0;
                const pageNumber =
                    totalPages === 0 ? 0 : Math.min(Number(rawNumber) || 0, Math.max(0, totalPages - 1));

                setData(content);
                setPagination((prev) => ({
                    ...prev,
                    page: pageNumber,
                    totalPages,
                    first: totalPages === 0 || pageNumber === 0,
                    last: totalPages === 0 || pageNumber >= totalPages - 1,
                }));
            } catch {
                if (!cancelled) {
                    setLoadError('Não foi possível carregar os centros de custo.');
                    setData([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [customerId, pagination.page, pageSize, sort, searchName, searchCode, searchActive, reloadTick]);

    const handlePageChange = (newPage) => {
        setPagination((prev) => ({ ...prev, page: newPage }));
    };

    const handleClearFilters = () => {
        setLocalName('');
        setLocalCode('');
        setLocalActive('true');
        setSearchName('');
        setSearchCode('');
        setSearchActive('true');
        setPagination((prev) => ({ ...prev, page: 0 }));
    };

    if (customerId == null || customerId === '') {
        return null;
    }

    const activeLabel = searchActive === 'true' ? 'Ativos' : 'Inativos';

    if (creating) {
        return (
            <CostCenterCreateForm
                customerId={customerId}
                onCancel={() => setCreating(false)}
                onSuccess={() => {
                    setCreating(false);
                    setPagination((prev) => ({ ...prev, page: 0 }));
                    setReloadTick((v) => v + 1);
                }}
            />
        );
    }

    if (detailItem) {
        return (
            <CostCenterDetailForm
                key={detailItem.id}
                customerId={customerId}
                costCenter={detailItem}
                onCancel={() => setDetailItem(null)}
                onSuccess={() => {
                    setDetailItem(null);
                    setReloadTick((v) => v + 1);
                }}
            />
        );
    }

    return (
        <CrudList
            title={title}
            tableMinWidth="640px"
            columns={COST_CENTER_LIST_COLUMNS}
            data={data}
            loading={loading}
            onNew={() => setCreating(true)}
            newButtonLabel="Novo centro de custo"
            actions={(item) => (
                <CrudOutlineButton
                    type="button"
                    variant="outline"
                    onClick={() => {
                        if (item?.id != null) {
                            setDetailItem(item);
                        }
                    }}
                >
                    Ver detalhes
                </CrudOutlineButton>
            )}
            pagination={{
                ...pagination,
                onPageChange: handlePageChange,
            }}
            filters={
                <>
                    <CostCenterSearchFilters
                        idPrefix={idPrefix}
                        nameValue={localName}
                        codeValue={localCode}
                        activeFilter={localActive}
                        onNameChange={setLocalName}
                        onCodeChange={setLocalCode}
                        onActiveFilterChange={setLocalActive}
                        onClearFilters={handleClearFilters}
                    />
                    <div
                        style={{
                            marginTop: 4,
                            padding: '10px 12px',
                            background: '#f8fafc',
                            borderRadius: 8,
                            border: '1px solid #e2e8f0',
                        }}
                    >
                        <p style={{ margin: 0, fontSize: 13, color: '#475569', lineHeight: 1.45 }}>
                            <strong style={{ color: '#334155' }}>Filtros aplicados:</strong> status{' '}
                            <strong>{activeLabel}</strong>
                            {searchName ? (
                                <>
                                    {' '}
                                    · nome contém <strong>&quot;{searchName}&quot;</strong>
                                </>
                            ) : null}
                            {searchCode ? (
                                <>
                                    {' '}
                                    · código contém <strong>&quot;{searchCode}&quot;</strong>
                                </>
                            ) : null}
                            {!searchName && !searchCode ? ' · sem filtro de texto' : null}
                        </p>
                    </div>
                    {loadError && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }} role="alert">
                            <p style={{ margin: 0, color: '#e74c3c', fontSize: 14 }}>{loadError}</p>
                            <button
                                type="button"
                                onClick={() => setReloadTick((v) => v + 1)}
                                style={{
                                    border: '1px solid #d1d5db',
                                    background: '#fff',
                                    borderRadius: 4,
                                    padding: '6px 10px',
                                    cursor: 'pointer',
                                }}
                            >
                                Tentar novamente
                            </button>
                        </div>
                    )}
                </>
            }
        />
    );
}
